import DataGrid, { DataGridConfig } from 'components/DataGrid';
import IconButton from 'components/IconButton';
import LoadingScreen from 'components/LoadingScreen';
import ModalDialog from 'components/ModalDialog';
import { useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { Suspense, useEffect, useState } from 'react';
import Select from 'react-select';
import { CircleSpinner } from 'react-spinners-kit';
import { Button, Row, Col, Card, CardBody } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { findingGridDataRefreshAtom } from '../state';

interface Props {
  onClose: () => void;
  postureIds: number[];
  isBulk?: boolean;
  summaryCard?: React.ReactNode;
}

interface Watcher {
  id: number;
  watcher: {
    id: number;
    users_id: number;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface Member {
  users_id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
}

const PCWatcherModal: React.FC<Props> = ({
  onClose,
  postureIds,
  isBulk = false,
  summaryCard,
}) => {
  const company_id = useAtomValue(companyIdAtom);
  const [loading, setLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isRemovingAll, setIsRemovingAll] = useState(false);
  const [_error, setError] = useState('');
  const [members, setMembers] = useState<Member[]>([]);
  const [currentWatcher, setCurrentWatcher] = useState<Member[]>([]);
  const [existingWatchers, setExistingWatchers] = useState<Watcher[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const findingGridDataRefresh = useSetAtom(findingGridDataRefreshAtom);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch both members and watchers
      const membersRes = await callApi(
        `/profiles/colleague/?search=&is_active=True&company=${company_id}`,
        'GET',
        {},
      );
      if (!isBulk) {
        const watchersRes = await callApi(`/pcm/posture-watchers/`, 'GET', {
          company_id: company_id,
          posture_ids: postureIds.join(','),
        });
        const fetchedWatchers =
          watchersRes.status === 200 ? watchersRes.data.results : [];
        setExistingWatchers(fetchedWatchers);
      }
      const fetchedMembers =
        membersRes.status === 200 ? membersRes.data.results : [];

      setMembers(
        fetchedMembers.map((member) => ({
          users_id: member.users_id,
          username: member.username,
          first_name: member.first_name,
          last_name: member.last_name,
          email: member.email,
        })),
      );
      setCurrentWatcher([]);
    } catch (err) {
      setError('Failed to fetch data');
      showFailureToast('Failed to fetch watcher data');
    } finally {
      setLoading(false);
    }
  };

  // Get filtered options for the Select component
  const getFilteredOptions = () => {
    const existingWatcherIds = existingWatchers.map((w) => w.watcher.id);
    return members.filter((m) => !existingWatcherIds.includes(m.users_id));
  };

  useEffect(() => {
    fetchData();
  }, [company_id]);

  const getWatcherGridConfig = (): DataGridConfig => ({
    rowIdKey: 'id',
    columns: [
      {
        key: 'id',
        label: 'ID',
        overrideContents: (rowData) => rowData.id,
      },
      {
        key: 'name',
        label: 'Name',
        overrideContents: (rowData) =>
          `${rowData.watcher.first_name} ${rowData.watcher.last_name}`,
      },
      {
        key: 'email',
        label: 'Email',
        overrideContents: (rowData) => rowData.watcher.email,
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (rowData) => (
          <IconButton
            id={`remove-watcher-${rowData.email}`}
            iconClass="fa fa-user-times"
            size="sm"
            tooltip="Remove Watcher"
            onClick={() => handleSave('remove', rowData.watcher.id)}
          />
        ),
        headerCellStyle: {
          textAlign: 'right',
          maxWidth: 'auto',
          minWidth: '150px !important',
        },
        rowCellStyle: {
          textAlign: 'right',
          minWidth: '150px !important',
          maxWidth: 'auto',
          whiteSpace: 'nowrap',
        },
      },
    ],
    columnsDisplayConfig: {
      enabledColumnKeys: ['name', 'email', 'actions'],
    },
    rowSelectionConfig: {
      preSelectedRows: selectedRowIds,
      onSelectionChange: (updatedRowIds) => {
        setSelectedRowIds(updatedRowIds);
      },
    },
  });

  const updateWatcherState = (actionType: string, watcherId?: number) => {
    if (actionType === 'assign') {
      // Add new watchers to the state
      const newWatchers = currentWatcher.map((watcher) => ({
        id: Math.random(), // Temporary ID for UI purposes
        watcher: {
          id: watcher.users_id,
          users_id: watcher.users_id,
          username: watcher.username,
          first_name: watcher.first_name,
          last_name: watcher.last_name,
          email: watcher.email,
        },
      }));
      setExistingWatchers([...existingWatchers, ...newWatchers]);
    } else {
      // Remove watchers from the state
      const watcherIdsToRemove = watcherId
        ? [watcherId]
        : existingWatchers
            .filter((watcher) => selectedRowIds.includes(watcher.id))
            .map((watcher) => watcher.watcher.id);
      setExistingWatchers(
        existingWatchers.filter(
          (watcher) => !watcherIdsToRemove.includes(watcher.watcher.id),
        ),
      );
    }
  };

  const handleSave = async (
    action: 'assign' | 'remove' | 'removeAll',
    watcherId?: number,
  ) => {
    if (action === 'assign') {
      setIsAssigning(true);
    } else if (action === 'remove') {
      setIsRemoving(true);
    } else {
      setIsRemovingAll(true);
    }
    setError('');

    try {
      const endpoint = action === 'assign' ? 'bulk_assign' : 'bulk_remove';
      let watcherIds: number[] = [];
      // For Single Finding
      if (!isBulk) {
        watcherIds =
          action === 'assign'
            ? currentWatcher.map((w) => w.users_id)
            : watcherId
            ? [watcherId]
            : existingWatchers
                .filter((watcher) => selectedRowIds.includes(watcher.id))
                .map((watcher) => watcher.watcher.id);
      } else {
        watcherIds = currentWatcher.map((w) => w.users_id);
      }

      // if removeAll, remove all watchers for the given posture ids
      if (isBulk && action === 'removeAll') {
        watcherIds = members.map((m) => m.users_id);
      }

      if (watcherIds.length === 0) {
        showFailureToast('No watchers selected');
        if (action === 'assign') {
          setIsAssigning(false);
        } else {
          setIsRemoving(false);
        }
        return;
      }

      const data = {
        posture_ids: postureIds,
        watcher_ids: watcherIds,
        company_id: company_id,
      };

      const response = await callApi(
        `/pcm/posture-watchers/${endpoint}/`,
        'POST',
        {},
        data,
      );

      if (response.status === 200 || response.status === 201) {
        const actionText = action === 'assign' ? 'assigned' : 'unassigned';
        showSuccessToast(`Watchers successfully ${actionText}`);
        if (!isBulk) {
          // Manually update the state instead of refetching
          updateWatcherState(action, watcherId);
        }
        // Refresh the data after successful operation
        findingGridDataRefresh(Math.random().toString());
        // Clear selections
        setSelectedRowIds([]);
        setCurrentWatcher([]);
      } else {
        setError(`Failed to ${action} watchers`);
        showFailureToast(`Failed to ${action} watchers`);
      }
    } catch (err) {
      setError(`Failed to ${action} watchers`);
      showFailureToast(`Failed to ${action} watchers`);
    } finally {
      if (action === 'assign') {
        setIsAssigning(false);
      } else if (action === 'remove') {
        setIsRemoving(false);
      } else {
        setIsRemovingAll(false);
      }
    }
  };

  const removeButton = () => {
    return (
      <Button
        color="secondary"
        onClick={() => handleSave('remove')}
        disabled={
          (isBulk && !currentWatcher.length) ||
          isAssigning ||
          isRemoving ||
          (!isBulk && selectedRowIds.length === 0)
        }
        className="d-flex align-items-center"
        style={{ marginRight: '10px' }}
      >
        {isRemoving ? (
          <>
            <CircleSpinner size={20} color="#fff" loading={true} />
            <span className="ml-2">Unassigning Watchers...</span>
          </>
        ) : isBulk && currentWatcher.length > 0 ? (
          `Unassign ${currentWatcher.length} ${
            currentWatcher.length === 1 ? 'Watcher' : 'Watchers'
          }`
        ) : !isBulk && selectedRowIds.length > 0 ? (
          `Unassign ${selectedRowIds.length} ${
            selectedRowIds.length === 1 ? 'Watcher' : 'Watchers'
          }`
        ) : (
          'Unassign Watchers'
        )}
      </Button>
    );
  };

  return (
    <ModalDialog
      style={{ maxWidth: '800px' }}
      size="lg"
      title={`Manage${
        postureIds.length === 1 ? ' Finding' : ' Findings'
      } Watchers`}
      subTitle={
        <p className="d-flex align-items-center">
          {isBulk && (
            <span>
              <strong>{postureIds.length}</strong>{' '}
              {postureIds.length === 1 ? 'Finding' : 'Findings'} selected for
              watcher assignment
            </span>
          )}
        </p>
      }
      childComponent={
        <Row>
          <Col>
            <Card className="p-2">
              <CardBody>
                {isBulk && (
                  <p>
                    Select the watchers for the selected findings. Click
                    assign/unassign button to add/remove the watchers.
                    <br />
                    <br />
                    Note: If the selected findings already have the assigned
                    watcher, that specific watcher will be skipped, and new
                    watchers will be added only. The same logic applies when
                    unassigning watchers.
                  </p>
                )}
                {!isBulk && (
                  <div className="mb-4">
                    {summaryCard}
                    <h5 className="mb-3">
                      <strong>Current Watchers</strong>
                    </h5>
                    <Suspense fallback={<LoadingScreen />}>
                      <div
                        style={{
                          minHeight: '200px',
                          maxHeight: '400px',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                        }}
                      >
                        <DataGrid
                          config={getWatcherGridConfig()}
                          data={existingWatchers}
                          loading={loading}
                        />
                      </div>
                      <div className="d-flex justify-content-end mt-1">
                        {existingWatchers.length > 0 && removeButton()}
                      </div>
                    </Suspense>
                  </div>
                )}

                <div className={isBulk ? 'mt-2' : 'mt-4'}>
                  <h5 className="mb-3">
                    <strong>
                      {isBulk ? 'Add/Remove Watchers' : 'Add New Watchers'}
                    </strong>
                  </h5>
                  <p className="text-muted mb-3">
                    {isBulk
                      ? 'Select members to add/remove as watchers'
                      : 'Select members to add as watchers. Current watchers are not shown in the list'}
                    .
                  </p>
                  <Select
                    className="custom-360-dropdown profile-pic-dropdown"
                    closeMenuOnSelect={false}
                    isMulti
                    isClearable
                    hideSelectedOptions
                    onChange={(selected) =>
                      setCurrentWatcher(selected as Member[])
                    }
                    value={currentWatcher}
                    options={isBulk ? members : getFilteredOptions()}
                    getOptionLabel={(option) =>
                      `${option.first_name} ${option.last_name} (${option.email})`
                    }
                    getOptionValue={(option) => option.users_id.toString()}
                    isDisabled={loading || isAssigning || isRemoving}
                    placeholder={
                      loading
                        ? 'Loading members...'
                        : 'Select members to add as watchers...'
                    }
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '38px',
                        maxHeight: '120px',
                        // overflow: 'auto',
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        maxHeight: '120px',
                        overflow: 'auto',
                        flexWrap: 'wrap',
                      }),
                      multiValue: (base) => ({
                        ...base,
                        margin: '2px',
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: '230px',
                        overflowY: 'auto',
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      }
      customFooter={
        <div className="d-flex justify-content-between w-100">
          <Button
            color="secondary"
            onClick={onClose}
            disabled={isAssigning || isRemoving}
          >
            Cancel
          </Button>
          <div className="d-flex align-items-center">
            {isBulk && (
              <Button
                color="danger"
                onClick={() => handleSave('removeAll')}
                disabled={loading || isAssigning || isRemoving || isRemovingAll}
                className="d-flex align-items-center"
                style={{ marginRight: '10px' }}
              >
                {isRemovingAll ? (
                  <>
                    <CircleSpinner size={20} color="#fff" loading={true} />
                    <span className="ml-2">Unassigning All Watchers...</span>
                  </>
                ) : (
                  'Unassign All Watchers'
                )}
              </Button>
            )}
            {isBulk && removeButton()}
            <Button
              color="primary"
              onClick={() => handleSave('assign')}
              disabled={!currentWatcher.length || isAssigning || isRemoving}
              className="d-flex align-items-center"
            >
              {isAssigning ? (
                <>
                  <CircleSpinner size={20} color="#fff" loading={true} />
                  <span className="ml-2">Assigning Watchers...</span>
                </>
              ) : currentWatcher.length > 0 ? (
                `Assign ${currentWatcher.length} ${
                  currentWatcher.length === 1 ? 'Watcher' : 'Watchers'
                }`
              ) : (
                'Assign Watchers'
              )}
            </Button>
          </div>
        </div>
      }
      onClose={onClose}
    />
  );
};

export default PCWatcherModal;
